import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useLoadScript, InfoWindow, Polygon, Polyline, OverlayView, useGoogleMap } from '@react-google-maps/api';
import { useSnackbar } from 'notistack';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  useTheme,
  TableSortLabel,
  TablePagination,
  Tabs,
  Tab,
  TextField,
  Button,
  Menu,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WifiIcon from '@mui/icons-material/Wifi';
import RouterIcon from '@mui/icons-material/Router';
import DevicesIcon from '@mui/icons-material/Devices';
import PeopleIcon from '@mui/icons-material/People';
import PowerIcon from '@mui/icons-material/Power';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LayersIcon from '@mui/icons-material/Layers';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExploreIcon from '@mui/icons-material/Explore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  getParentLatencyMs,
  getCapLatencyMs,
  getWorstLatencyMs,
  getLatencyHexColor,
  formatLatencyMs,
  formatLatencyTooltip,
} from '../services/latency';
import { api } from '../services/api';
import {
  isNodeDevice,
  isCapDevice,
  deviceMapCoordinates,
  hasPublicWifi,
  buildRrmPlanIndex,
  rrmSerialKey,
  createRrmMarkerDiv,
} from '../utils/publicWifiRrmPlanUi';
import { useMapPolygonDrawing } from '../hooks/useMapPolygonDrawing';
import { MAP_UI, publicWifiStatSx, publicWifiMapActionBtnSx } from '../theme/mapViewUi';
import PublicWifiMapSelectionDialog from './PublicWifiMapSelectionDialog';
import FieldSurveyTab from './FieldSurveyTab';
import {
  ASSOC_HISTORY_MAX_ROWS,
  HIST_DATA_SIZE_UNITS,
  buildNetworkingAssocHistoryFilter,
  looksLikeClientMac,
  parseHistNumericFilters,
} from '../utils/publicWifiAssocHistory';

/** Devices tab map markers — Public WiFi: light blue; excluded critical: light red. */
function getOverviewMapMarkerStyle(serial, publicWifiAnySerials) {
  const s = rrmSerialKey(serial);
  if (publicWifiAnySerials.has(s)) {
    return { color: MAP_UI.rrmPlanMarker, label: 'W' };
  }
  return { color: MAP_UI.deviceOnline, label: 'N' };
}

function getOverviewExcludedMarkerStyle(serial, excludedSerialSet, publicWifiAnySerials) {
  const s = rrmSerialKey(serial);
  // Deployed Public WiFi wins over plan exclusion — show W, not X/!!.
  if (publicWifiAnySerials.has(s)) return null;
  if (!excludedSerialSet.has(s)) return null;
  return {
    color: MAP_UI.deviceOffline,
    label: 'X',
  };
}

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];

const MAP_FILTER_PUBLIC_WIFI = 'public_wifi_enabled';
const MAP_FILTER_EXCLUDED_CRITICAL = 'excluded_critical';

const MAP_DEVICE_FILTER_OPTIONS = [
  { id: MAP_FILTER_PUBLIC_WIFI, label: 'Public WiFi enabled', detail: 'Lamps with Public WiFi active (CAPs always shown)' },
  { id: MAP_FILTER_EXCLUDED_CRITICAL, label: 'Excluded critical', detail: 'Critical tree parents (RRM plan); CAPs always shown' },
];

function publicWifiEntryHasApSsid(entry) {
  if (!entry?.interfaces) return false;
  return entry.interfaces.some((iface) => {
    if ((iface.mode || '').toLowerCase() !== 'ap') return false;
    return normalizeSsidKey(iface.ssid) !== '';
  });
}

/** True when device still shows Public WiFi from live state (respects post-removal UI override). */
function deviceReportsPublicWifi(serial, device, publicWifiMap, clearedSerials) {
  if (clearedSerials.has(serial)) return false;
  if (hasPublicWifi(device)) return true;
  return publicWifiEntryHasApSsid(publicWifiMap[serial]);
}

function normalizeSsidKey(ssid) {
  if (!ssid || typeof ssid !== 'string') return '';
  const trimmed = ssid.trim().toLowerCase();
  if (trimmed === 'n/a' || trimmed === '-' || trimmed === '') return '';
  return trimmed;
}

function profileMatchesLiveSsid(profile, liveSsid) {
  const profileKey = normalizeSsidKey(profile?.ssid || profile?.name);
  const liveKey = normalizeSsidKey(liveSsid);
  return profileKey && liveKey && profileKey === liveKey;
}

function groupHasSsidProfile(group, profileId) {
  const pid = String(profileId);
  if ((group?.ssid_ids || []).some((id) => String(id) === pid)) return true;
  return (group?.ssids || []).some((s) => String(s.id) === pid);
}

function deviceInGroup(group, serial) {
  return (group?.devices || []).some((s) => String(s).toLowerCase() === serial);
}

/** Resolve how to remove Public WiFi SSID from a device (direct unassign vs remove from group). */
function resolveSsidRemovalAction(device, liveSsid, ssidProfiles, groups) {
  if (!device || !liveSsid) return null;
  const serial = (device.serial_number || '').toLowerCase();
  if (!serial) return null;

  const matchingProfiles = (ssidProfiles || []).filter((p) => profileMatchesLiveSsid(p, liveSsid));
  if (matchingProfiles.length === 0) {
    return { type: 'unknown', ssidName: liveSsid };
  }

  for (const profile of matchingProfiles) {
    const directSerials = (profile.device_serials || []).map((s) => String(s).toLowerCase());
    if (directSerials.includes(serial)) {
      return {
        type: 'direct',
        ssidName: liveSsid,
        profileId: profile.id,
        profileName: profile.ssid || profile.name || liveSsid,
      };
    }
  }

  for (const group of groups || []) {
    if (!deviceInGroup(group, serial)) continue;
    for (const profile of matchingProfiles) {
      if (groupHasSsidProfile(group, profile.id)) {
        return {
          type: 'group',
          ssidName: liveSsid,
          profileId: profile.id,
          profileName: profile.ssid || profile.name || liveSsid,
          groupId: group.id,
          groupName: group.name || group.id,
        };
      }
    }
  }

  for (const profile of matchingProfiles) {
    for (const groupId of profile.group_ids || []) {
      const group = (groups || []).find((g) => String(g.id) === String(groupId));
      if (group && deviceInGroup(group, serial)) {
        return {
          type: 'group',
          ssidName: liveSsid,
          profileId: profile.id,
          profileName: profile.ssid || profile.name || liveSsid,
          groupId: group.id,
          groupName: group.name || group.id,
        };
      }
    }
  }

  return { type: 'unknown', ssidName: liveSsid };
}

function formatMapDevicesBanner(markers, filters, excludedSerialSet, publicWifiAnySerials) {
  const count = markers.length;
  const capCount = markers.filter((d) => isCapDevice(d)).length;
  const lampCount = count - capCount;
  const deviceWord = count === 1 ? 'marker' : 'markers';
  const onlyPublicWifi = filters.size === 1 && filters.has(MAP_FILTER_PUBLIC_WIFI);
  const onlyExcluded = filters.size === 1 && filters.has(MAP_FILTER_EXCLUDED_CRITICAL);
  const capSuffix = capCount > 0 ? `, ${capCount} CAP${capCount === 1 ? '' : 's'}` : '';
  const excludedCriticalLampCount = markers.filter((d) => {
    if (isCapDevice(d)) return false;
    const serial = (d.serial_number || '').toLowerCase();
    return excludedSerialSet.has(serial) && !publicWifiAnySerials.has(serial);
  }).length;
  if (onlyPublicWifi) {
    return `${count} ${deviceWord} — ${lampCount} lamp${lampCount === 1 ? '' : 's'} with Public WiFi enabled${capSuffix}, based on device report`;
  }
  if (onlyExcluded) {
    return `${count} ${deviceWord} — ${excludedCriticalLampCount} excluded critical (RRM plan)${capSuffix}, based on device report`;
  }
  const labels = MAP_DEVICE_FILTER_OPTIONS.filter((o) => filters.has(o.id)).map((o) => o.label).join(' · ');
  return `${count} ${deviceWord} — ${labels}${capSuffix}`;
}

const defaultCenter = {
  lat: 32.0853, // Tel Aviv, Israel (default center)
  lng: 34.7818
};

const formatBytes = (n) => {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '—';
  let v = Number(n);
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return i === 0 ? `${Math.round(v)} ${units[i]}` : `${v >= 10 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
};

const formatConnectedTime = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const s = Math.max(0, Math.floor(Number(sec)));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const formatSeenAt = (iso) => {
  if (iso == null || iso === '') return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

/** PHY bitrate from association payloads: kilobits per second (device + mesh code use kbps). */
const formatBitrateKbitMbit = (bitrateKbps) => {
  if (bitrateKbps == null || !Number.isFinite(Number(bitrateKbps))) return null;
  const kbps = Number(bitrateKbps);
  const mbps = kbps / 1000;
  if (mbps >= 1) {
    return mbps >= 10 ? `${mbps.toFixed(0)} Mbit/s` : `${mbps.toFixed(1)} Mbit/s`;
  }
  if (kbps >= 1) {
    return kbps >= 100 ? `${kbps.toFixed(0)} Kbit/s` : `${kbps.toFixed(1)} Kbit/s`;
  }
  if (kbps > 0) {
    return `${kbps} Kbit/s`;
  }
  return null;
};

/** Public WiFi history table: human-readable bitrate + channel width (MHz). */
const formatWifiRateDisplay = (rate) => {
  if (!rate || typeof rate !== 'object') return '—';
  const br = formatBitrateKbitMbit(rate.bitrate);
  const cw = rate.chwidth;
  const chOk = cw != null && Number.isFinite(Number(cw));
  const chStr = chOk ? `${cw} MHz` : null;
  if (br == null && chStr == null) return '—';
  if (br == null) return `— · ${chStr}`;
  if (chStr == null) return `${br} · —`;
  return `${br} · ${chStr}`;
};

const csvEscapeCell = (value) => {
  if (value == null || value === '') return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (filename, headerRow, dataRows) => {
  const lines = [headerRow.map(csvEscapeCell).join(','), ...dataRows.map((cells) => cells.map(csvEscapeCell).join(','))];
  const body = lines.join('\r\n');
  const blob = new Blob([`\uFEFF${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const parseRssiNum = (v) => {
  if (v === null || v === undefined || v === 'N/A') return null;
  const n = typeof v === 'string' ? parseInt(v, 10) : v;
  return Number.isFinite(n) ? n : null;
};

const getRssiColorForClient = (rssiValue) => {
  if (rssiValue === null) return '#9e9e9e';
  if (rssiValue >= -70) return MAP_UI.throughputExcellent;
  if (rssiValue >= -80) return MAP_UI.moderate;
  return MAP_UI.throughputPoor;
};

// Custom AdvancedMarker — create once, patch props in place (avoids flicker on parent re-render).
const AdvancedMarker = ({ position, content, onClick, title, zIndex }) => {
  const markerRef = React.useRef(null);
  const clickListenerRef = React.useRef(null);
  const onClickRef = React.useRef(onClick);
  const map = useGoogleMap();

  onClickRef.current = onClick;

  const attachClickListener = React.useCallback((marker) => {
    if (!marker || !window.google?.maps?.event) return;
    if (clickListenerRef.current) {
      window.google.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
    clickListenerRef.current = marker.addListener('click', () => {
      onClickRef.current?.();
    });
  }, []);

  React.useEffect(() => {
    if (!window.google?.maps?.marker?.AdvancedMarkerElement || !map) {
      return undefined;
    }
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      title,
      map,
      zIndex,
    });
    markerRef.current = marker;
    attachClickListener(marker);
    return () => {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
    // Marker is created once; position/content/title/zIndex are patched in place below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, attachClickListener]);

  React.useEffect(() => {
    if (markerRef.current && position) {
      markerRef.current.position = position;
    }
  }, [position]);

  React.useEffect(() => {
    if (markerRef.current && content) {
      markerRef.current.content = content;
    }
  }, [content]);

  React.useEffect(() => {
    if (markerRef.current) {
      markerRef.current.title = title || '';
    }
  }, [title]);

  React.useEffect(() => {
    if (markerRef.current && zIndex != null) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  return null;
};

const PublicWifiOverview = ({ devices = [], isActive = true, onRefresh, readOnly = false }) => {
  const canMutate = !readOnly;
  const navigate = useNavigate();
  const theme = useTheme();
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMapDevice, setSelectedMapDevice] = useState(null);
  const [polygonSelectedDevices, setPolygonSelectedDevices] = useState([]);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [publicWifiMap, setPublicWifiMap] = useState({});
  const [publicWifiLoading, setPublicWifiLoading] = useState(true);
  /** Serials optimistically cleared after Remove SSID — until device state report catches up. */
  const [ssidClearedSerials, setSsidClearedSerials] = useState(() => new Set());
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [overviewTab, setOverviewTab] = useState(0);
  const [rrmPlanData, setRrmPlanData] = useState(null);
  const [mapDeviceFilters, setMapDeviceFilters] = useState(() => new Set([MAP_FILTER_PUBLIC_WIFI]));
  const [showDevicesMenuAnchor, setShowDevicesMenuAnchor] = useState(null);
  const [showConnections, setShowConnections] = useState(false);
  const [ssidProfiles, setSsidProfiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [removeSsidDialogOpen, setRemoveSsidDialogOpen] = useState(false);
  const [removeSsidTarget, setRemoveSsidTarget] = useState(null);
  const [removeSsidSaving, setRemoveSsidSaving] = useState(false);
  const [clientSortCol, setClientSortCol] = useState('clientMac');
  const [clientSortDir, setClientSortDir] = useState('asc');
  const [clientPage, setClientPage] = useState(0);
  const [clientRowsPerPage, setClientRowsPerPage] = useState(25);
  const [liveMacInput, setLiveMacInput] = useState('');
  const [liveMacQuery, setLiveMacQuery] = useState('');
  const [liveMacError, setLiveMacError] = useState('');
  const [histPreset, setHistPreset] = useState('24h');
  const [histCustomFrom, setHistCustomFrom] = useState('');
  const [histCustomTo, setHistCustomTo] = useState('');
  const [histSerialFilter, setHistSerialFilter] = useState('');
  const [histStationFilter, setHistStationFilter] = useState('');
  const [histMinConnectedTime, setHistMinConnectedTime] = useState('');
  const [histMaxConnectedTime, setHistMaxConnectedTime] = useState('');
  const [histMinRxBytes, setHistMinRxBytes] = useState('');
  const [histMinTxBytes, setHistMinTxBytes] = useState('');
  const [histDataSizeUnit, setHistDataSizeUnit] = useState('bytes');
  const [histRows, setHistRows] = useState([]);
  const [histSnapshotCount, setHistSnapshotCount] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histLoadProgress, setHistLoadProgress] = useState(null);
  const [histLoadPhase, setHistLoadPhase] = useState('');
  const [histError, setHistError] = useState(null);
  const [histHasQueried, setHistHasQueried] = useState(false);
  const [histFiltersOpen, setHistFiltersOpen] = useState(true);
  const [histSortCol, setHistSortCol] = useState('reported_at');
  const [histSortDir, setHistSortDir] = useState('desc');
  const mapRef = useRef(null);
  const mapViewportRef = useRef({ center: defaultCenter, zoom: 12 });
  const hasFittedToDevicesRef = useRef(false);
  const hadMapDevicesRef = useRef(false);
  const isFittingMapRef = useRef(false);
  const [viewportLocked, setViewportLocked] = useState(false);
  const markerContentCacheRef = useRef(new Map());
  const markerClickBySerialRef = useRef(new Map());
  const mapDeviceBySerialRef = useRef(new Map());
  const markerClickStateRef = useRef({ drawingMode: false });

  // Load Google Maps (optional - page works without it)
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const mapColorScheme = React.useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) {
      return undefined;
    }
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);

  React.useEffect(() => {
    if (!mapRef.current || !mapColorScheme) {
      return;
    }
    mapRef.current.setOptions({ colorScheme: mapColorScheme });
  }, [mapColorScheme]);

  const fetchPublicWifi = useCallback(() => {
    setPublicWifiLoading(true);
    const mac = looksLikeClientMac(liveMacQuery) ? liveMacQuery.trim() : undefined;
    api.getAllPublicWifi({ mac })
      .then(data => {
        const map = {};
        (data || []).forEach(entry => {
          map[entry.serial] = entry;
        });
        setPublicWifiMap(map);
        setSsidClearedSerials((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          prev.forEach((serial) => {
            if (!publicWifiEntryHasApSsid(map[serial])) {
              next.delete(serial);
            }
          });
          return next.size === prev.size ? prev : next;
        });
      })
      .catch((err) => console.error('Failed to fetch public wifi data:', err))
      .finally(() => setPublicWifiLoading(false));
  }, [liveMacQuery]);

  useEffect(() => {
    fetchPublicWifi();
  }, [fetchPublicWifi]);

  const fetchSsidProfilesAndGroups = useCallback(() => {
    Promise.all([
      api.getSSIDProfiles().catch(() => ({})),
      api.getGroups(true).catch(() => ({})),
    ]).then(([ssidRes, groupRes]) => {
      setSsidProfiles(Array.isArray(ssidRes?.ssids) ? ssidRes.ssids : []);
      setGroups(Array.isArray(groupRes?.groups) ? groupRes.groups : []);
    });
  }, []);

  useEffect(() => {
    if (!isActive) return;
    fetchSsidProfilesAndGroups();
  }, [isActive, fetchSsidProfilesAndGroups]);

  const histRange = useMemo(() => {
    if (histPreset === 'custom') {
      const f = histCustomFrom ? new Date(histCustomFrom) : null;
      const t = histCustomTo ? new Date(histCustomTo) : null;
      if (f && t && t > f) return { from: f.toISOString(), to: t.toISOString() };
      return null;
    }
    const to = new Date();
    const from = new Date(to.getTime());
    if (histPreset === '1h') from.setHours(from.getHours() - 1);
    else if (histPreset === '24h') from.setHours(from.getHours() - 24);
    else if (histPreset === '7d') from.setDate(from.getDate() - 7);
    else if (histPreset === '30d') from.setDate(from.getDate() - 30);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [histPreset, histCustomFrom, histCustomTo]);

  const fetchHistHistory = useCallback(async () => {
    if (!histRange) {
      setHistError('Select a valid time window (To must be after From).');
      return;
    }
    setHistLoading(true);
    setHistLoadProgress(5);
    setHistLoadPhase('Counting distinct clients…');
    setHistError(null);
    setHistSnapshotCount(null);

    const numeric = parseHistNumericFilters({
      minConnectedTime: histMinConnectedTime,
      maxConnectedTime: histMaxConnectedTime,
      minRxBytes: histMinRxBytes,
      minTxBytes: histMinTxBytes,
      dataSizeUnit: histDataSizeUnit,
    });
    if (numeric.error) {
      setHistLoading(false);
      setHistLoadProgress(null);
      setHistLoadPhase('');
      setHistError(numeric.error);
      return;
    }

    const serialFilt = histSerialFilter.trim().toLowerCase();
    const stationRaw = histStationFilter.trim();
    let stationDigest = '';
    if (stationRaw) {
      if (!looksLikeClientMac(stationRaw)) {
        setHistLoading(false);
        setHistLoadProgress(null);
        setHistLoadPhase('');
        setHistError('Enter a full client MAC (aa:bb:cc:dd:ee:ff).');
        return;
      }
    }
    const filter = buildNetworkingAssocHistoryFilter(histRange.from, histRange.to, {
      serial: serialFilt || undefined,
      minConnected: numeric.minConnected,
      maxConnected: numeric.maxConnected,
      minRx: numeric.minRx,
      minTx: numeric.minTx,
    });
    try {
      if (stationRaw) {
        const digestRes = await api.digestPublicWifiClientMac(stationRaw);
        stationDigest = digestRes?.digest || '';
      }
      const totalCount = await api.queryNetworkingAssocHistoryCount(filter);
      setHistLoadProgress(45);
      setHistSnapshotCount(totalCount);
      if (totalCount <= 0) {
        setHistRows([]);
        setHistHasQueried(true);
        setHistLoadProgress(100);
        return;
      }
      setHistLoadPhase('Loading client records…');
      setHistLoadProgress(55);
      const rows = await api.queryNetworkingAssocHistoryRows(filter, {
        serial: serialFilt,
        station: stationRaw,
        stationDigest,
        minConnected: numeric.minConnected,
        maxConnected: numeric.maxConnected,
        minRx: numeric.minRx,
        minTx: numeric.minTx,
      });
      setHistLoadProgress(95);
      setHistRows(rows);
      setHistHasQueried(true);
      setHistLoadProgress(100);
    } catch (err) {
      setHistSnapshotCount(null);
      setHistError(err?.message || String(err));
    } finally {
      setHistLoading(false);
      setHistLoadProgress(null);
      setHistLoadPhase('');
    }
  }, [
    histRange,
    histSerialFilter,
    histStationFilter,
    histMinConnectedTime,
    histMaxConnectedTime,
    histMinRxBytes,
    histMinTxBytes,
    histDataSizeUnit,
  ]);

  useEffect(() => {
    if (overviewTab !== 3) return;
    setHistHasQueried(false);
    setHistRows([]);
    setHistSnapshotCount(null);
    setHistLoadProgress(null);
    setHistLoadPhase('');
    setHistError(null);
  }, [overviewTab]);

  const histActiveFilterCount = useMemo(() => {
    let count = 0;
    if (histPreset !== '24h') count += 1;
    if (histSerialFilter.trim()) count += 1;
    if (histStationFilter.trim()) count += 1;
    if (histMinConnectedTime.trim()) count += 1;
    if (histMaxConnectedTime.trim()) count += 1;
    if (histMinRxBytes.trim()) count += 1;
    if (histMinTxBytes.trim()) count += 1;
    return count;
  }, [
    histPreset,
    histSerialFilter,
    histStationFilter,
    histMinConnectedTime,
    histMaxConnectedTime,
    histMinRxBytes,
    histMinTxBytes,
  ]);

  const clearHistFilters = useCallback(() => {
    setHistPreset('24h');
    setHistCustomFrom('');
    setHistCustomTo('');
    setHistSerialFilter('');
    setHistStationFilter('');
    setHistMinConnectedTime('');
    setHistMaxConnectedTime('');
    setHistMinRxBytes('');
    setHistMinTxBytes('');
    setHistDataSizeUnit('bytes');
  }, []);

  const deviceNameBySerial = useMemo(() => {
    const m = {};
    (devices || []).forEach((d) => {
      const s = (d.serial_number || '').toLowerCase();
      if (s) m[s] = d.name || d.serial_number;
    });
    return m;
  }, [devices]);

  const isNonEmptyString = useCallback((value) => typeof value === 'string' && value.trim().length > 0, []);

  const isValidSsid = useCallback((ssid) => {
    if (!isNonEmptyString(ssid)) return false;
    const trimmed = ssid.trim();
    const lower = trimmed.toLowerCase();
    return lower !== 'n/a' && lower !== '-' && trimmed !== '';
  }, [isNonEmptyString]);

  // Get device type helper
  const getDeviceType = useCallback((device) => {
    if (!device.capabilities) return 'Unknown';
    const model = device.capabilities.model || '';
    if (model.toLowerCase().includes('nvidia') || model.toLowerCase().includes('jetson') || model.toLowerCase().includes('qrb5165')) {
      return 'AI';
    }
    if (model.toLowerCase().includes('cap')) return 'CAP';
    if (model.toLowerCase().includes('node')) return 'Node';
    return model || 'Unknown';
  }, []);

  // Get connected clients count from the dedicated public-wifi endpoint (source of truth)
  // CRITICAL: Offline devices MUST have zero clients - do not show stale data
  const getConnectedClients = useCallback((device) => {
    if (!device?.connected) return 0;
    const serial = (device?.serial_number || '').toLowerCase();
    const entry = publicWifiMap[serial];
    if (!entry?.interfaces) return 0;
    return entry.interfaces.reduce((sum, iface) => sum + (iface.num_clients || 0), 0);
  }, [publicWifiMap]);

  // Get SSID from GET /public-wifi (device state report); cleared locally after Remove SSID until report updates.
  const getSSID = useCallback((device) => {
    const serial = (device?.serial_number || '').toLowerCase();
    if (ssidClearedSerials.has(serial)) return null;
    const entry = publicWifiMap[serial];
    if (!entry?.interfaces) return null;
    for (const iface of entry.interfaces) {
      if ((iface.mode || '').toLowerCase() !== 'ap') continue;
      const ssid = iface.ssid;
      if (isValidSsid(ssid)) return ssid;
    }
    return null;
  }, [publicWifiMap, isValidSsid, ssidClearedSerials]);

  // TX Failed (%): use device.backhaul_tx_failed_percentage exactly (0.0-1.0), as on Device page
  const getTxFailed = useCallback((device) => {
    const raw = device?.backhaul_tx_failed_percentage;
    if (raw === undefined || raw === null || raw === 'N/A') return null;
    const value = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (!Number.isFinite(value)) return null;
    return value;
  }, []);

  // Connection RSSI details: copied from DeviceDetails.getConnectionDetails logic for parity
  const getConnectionDetails = (deviceToCheck, parentDeviceToCheck) => {
    if (!deviceToCheck || !parentDeviceToCheck) return null;

    // Get all identifiers: MACs from radios AND serial number
    // jnet2 uses serial number in associations, EdgeCore uses MAC
    const deviceMacs = deviceToCheck.radios?.map((radio) => radio.mac?.toLowerCase()).filter(Boolean) || [];
    const deviceSerial = deviceToCheck.serial_number?.toLowerCase();

    const deviceIdentifiers = new Set(deviceMacs);
    if (deviceSerial) {
      deviceIdentifiers.add(deviceSerial);
      deviceIdentifiers.add(deviceSerial.replace(/:/g, ''));
    }

    if (deviceIdentifiers.size === 0) return null;

    const searchInterfaces = (interfaces) => {
      if (!interfaces) return null;
      for (const iface of interfaces) {
        const associations = iface.Associations || iface.associations;
        if (!associations) continue;
        for (const assoc of associations) {
          const station = assoc.Station || assoc.station;
          if (!station) continue;

          const stationId = station.toLowerCase().replace(/:/g, '');
          const stationLower = station.toLowerCase();

          const rssiValue = assoc.RSSI || assoc.rssi;

          if (deviceIdentifiers.has(stationId) || deviceIdentifiers.has(stationLower)) {
            return { rssi: rssiValue };
          }
        }
      }
      return null;
    };

    const getStationRssi = (device) => {
      const privateIfaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces;
      if (!privateIfaces) return null;
      const stationInterface = privateIfaces.find((iface) => (iface.Mode || iface.mode)?.toLowerCase() === 'station');
      return stationInterface?.RSSI || stationInterface?.rssi;
    };

    const parentSerial = (parentDeviceToCheck?.serial_number || '').toLowerCase();
    const parentPublicWifiEntry = publicWifiMap[parentSerial];
    const connectionDetails =
      searchInterfaces(parentDeviceToCheck.private_wifi?.interfaces || parentDeviceToCheck.private_wifi?.Interfaces) ||
      searchInterfaces(parentPublicWifiEntry?.interfaces);

    if (!connectionDetails) return null;

    return {
      ...connectionDetails,
      stationRssi: getStationRssi(deviceToCheck),
    };
  };

  // Match DeviceDetails: prefer the child's persisted `uplink` RSSI (same source
  // as the optimizer) because GET /devices strips WiFi associations from both
  // private_wifi and public_wifi — parent-side association lookup is usually empty.
  // GET /public-wifi only carries public-SSID client associations, not mesh backhaul.
  const getUplinkDetails = (childDevice, parentDevice) => {
    const fallback = getConnectionDetails(childDevice, parentDevice);
    const u = childDevice?.uplink;
    if (!u || (!u.tx_profile && !u.rx_profile && !u.rssi)) {
      return fallback;
    }
    const childRssi = u.rssi != null && u.rssi !== '' ? u.rssi : null;
    return {
      rssi: childRssi || fallback?.rssi || null,
      stationRssi: childRssi || fallback?.stationRssi || null,
    };
  };

  // Devices that appear in GET /public-wifi are the authoritative set.
  // Cross-reference with the full devices list to get metadata (connected,
  // GPS, latency, etc.) that the dedicated endpoint does not include.
  const devicesWithPublicWifi = useMemo(() => {
    if (!devices || !Array.isArray(devices)) return [];
    return devices.filter((device) => {
      const serial = (device?.serial_number || '').toLowerCase();
      return deviceReportsPublicWifi(serial, device, publicWifiMap, ssidClearedSerials);
    });
  }, [devices, publicWifiMap, ssidClearedSerials]);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    api.getPublicWifiRRMPlan()
      .then((data) => { if (!cancelled && data) setRrmPlanData(data); })
      .catch((err) => {
        if (!cancelled && err?.statusCode !== 404) console.error('Failed to load Wi‑Fi RRM plan:', err);
      });
    return () => { cancelled = true; };
  }, [isActive]);

  const rrmPlanIndex = useMemo(() => buildRrmPlanIndex(rrmPlanData), [rrmPlanData]);

  const publicWifiAnySerials = useMemo(() => {
    const set = new Set();
    if (!devices?.length) return set;
    devices.forEach((d) => {
      const serial = (d.serial_number || '').toLowerCase();
      if (!serial) return;
      if (deviceReportsPublicWifi(serial, d, publicWifiMap, ssidClearedSerials)) set.add(serial);
    });
    return set;
  }, [devices, publicWifiMap, ssidClearedSerials]);

  const toggleMapDeviceFilter = useCallback((key) => {
    setMapDeviceFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /** Map markers: CAPs with GPS (always when any layer is on) ∪ NODEs matching selected filters. */
  const mapRrmDevices = useMemo(() => {
    if (!devices?.length || mapDeviceFilters.size === 0) return [];
    const showPw = mapDeviceFilters.has(MAP_FILTER_PUBLIC_WIFI);
    const showExcluded = mapDeviceFilters.has(MAP_FILTER_EXCLUDED_CRITICAL);
    const list = [];
    const seen = new Set();

    const pushWithGps = (device) => {
      const serial = (device.serial_number || '').toLowerCase();
      if (!serial || seen.has(serial)) return;
      const coords = deviceMapCoordinates(device);
      if (!coords) return;
      seen.add(serial);
      list.push({ ...device, lat: coords.lat, lng: coords.lng });
    };

    for (const device of devices) {
      if (isCapDevice(device)) pushWithGps(device);
    }

    for (const device of devices) {
      if (!isNodeDevice(device)) continue;
      const serial = (device.serial_number || '').toLowerCase();
      if (!serial || seen.has(serial)) continue;

      const hasPwEnabled = deviceReportsPublicWifi(serial, device, publicWifiMap, ssidClearedSerials);
      const isExcluded = rrmPlanIndex.excludedSerialSet.has(serial);

      const includePw = showPw && hasPwEnabled;
      const includeExcluded = showExcluded && isExcluded;

      if (includePw || includeExcluded) {
        pushWithGps(device);
      }
    }
    return list;
  }, [devices, publicWifiMap, rrmPlanIndex, mapDeviceFilters, ssidClearedSerials]);

  const visibleMapSerials = useMemo(
    () => new Set(mapRrmDevices.map((d) => (d.serial_number || '').toLowerCase()).filter(Boolean)),
    [mapRrmDevices],
  );

  const gpsLookup = useMemo(() => {
    const map = {};
    if (!devices || !Array.isArray(devices)) return map;
    devices.forEach((d) => {
      const serial = (d.serial_number || '').toLowerCase();
      if (!serial) return;
      const coords = deviceMapCoordinates(d);
      if (!coords) return;
      map[serial] = { lat: coords.lat, lng: coords.lng, device: d };
    });
    return map;
  }, [devices]);

  const connectionLines = useMemo(() => {
    if (!showConnections || !devices || !Array.isArray(devices)) return [];
    const lines = [];
    devices.forEach((d) => {
      if (!d.parent_serial) return;
      const serial = (d.serial_number || '').toLowerCase();
      if (!visibleMapSerials.has(serial)) return;
      const parentSerial = d.parent_serial.toLowerCase();
      const me = gpsLookup[serial];
      const par = gpsLookup[parentSerial];
      if (!me || !par) return;

      const R = 6371000;
      const dLat = (par.lat - me.lat) * Math.PI / 180;
      const dLng = (par.lng - me.lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(me.lat * Math.PI / 180) * Math.cos(par.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
      const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      if (distance === 0) return;

      const deviceMacs = (d.radios || []).map((r) => r.mac?.toLowerCase()).filter(Boolean);
      const deviceIds = new Set([serial, serial.replace(/:/g, ''), ...deviceMacs]);
      const searchInterfaces = (interfaces) => {
        if (!interfaces) return null;
        for (const iface of interfaces) {
          const assocs = iface.Associations || iface.associations;
          if (!assocs) continue;
          for (const assoc of assocs) {
            const station = (assoc.Station || assoc.station || '').toLowerCase();
            if (deviceIds.has(station) || deviceIds.has(station.replace(/:/g, ''))) {
              const rssi = assoc.RSSI || assoc.rssi;
              return rssi != null ? parseInt(rssi, 10) : null;
            }
          }
        }
        return null;
      };
      const rssi = searchInterfaces(par.device.private_wifi?.interfaces || par.device.private_wifi?.Interfaces)
        ?? searchInterfaces(par.device.public_wifi?.interfaces || par.device.public_wifi?.Interfaces);

      const color = rssi != null
        ? (rssi >= -85 ? MAP_UI.throughputExcellent : MAP_UI.throughputPoor)
        : MAP_UI.rrmPlanMarker;
      const distText = distance < 1000 ? `${Math.round(distance)}m` : `${(distance / 1000).toFixed(2)}km`;
      const label = rssi != null ? `${rssi} dBm` : distText;

      lines.push({
        key: `conn-${serial}`,
        from: me,
        to: par,
        color,
        label,
        midLat: (me.lat + par.lat) / 2,
        midLng: (me.lng + par.lng) / 2,
        rssi,
        distText,
      });
    });
    return lines;
  }, [showConnections, devices, gpsLookup, visibleMapSerials]);

  const handlePolygonSelectionComplete = useCallback((matchedDevices) => {
    setPolygonSelectedDevices(matchedDevices);
    setSelectionDialogOpen(true);
    setSelectedMapDevice(null);
  }, []);

  const handlePolygonEmptySelection = useCallback((message) => {
    enqueueSnackbar(message, { variant: 'info' });
  }, [enqueueSnackbar]);

  const {
    drawingMode,
    toggleDrawingMode,
    polygons,
    clearPolygons,
  } = useMapPolygonDrawing({
    mapRef,
    devicesWithLocation: mapRrmDevices,
    onSelectionComplete: handlePolygonSelectionComplete,
    onEmptySelection: handlePolygonEmptySelection,
  });

  const handleSelectionDialogClose = useCallback(() => {
    setSelectionDialogOpen(false);
    setPolygonSelectedDevices([]);
    clearPolygons();
  }, [clearPolygons]);

  const handleOpenRemoveSsidDialog = useCallback((device) => {
    const liveSsid = getSSID(device);
    if (!liveSsid) return;
    const action = resolveSsidRemovalAction(device, liveSsid, ssidProfiles, groups);
    setRemoveSsidTarget({ device, action });
    setRemoveSsidDialogOpen(true);
  }, [getSSID, ssidProfiles, groups]);

  const handleCloseRemoveSsidDialog = useCallback(() => {
    if (removeSsidSaving) return;
    setRemoveSsidDialogOpen(false);
    setRemoveSsidTarget(null);
  }, [removeSsidSaving]);

  const handleConfirmRemoveSsid = useCallback(async () => {
    const target = removeSsidTarget;
    if (!target?.device || !target?.action || removeSsidSaving) return;
    const { device, action } = target;
    const serial = (device.serial_number || '').toLowerCase();
    if (!serial) return;

    if (action.type === 'unknown') {
      enqueueSnackbar('Could not determine how this SSID is assigned. Use WiFi Network → Configuration to manage assignments.', { variant: 'warning' });
      return;
    }

    setRemoveSsidSaving(true);
    try {
      if (action.type === 'direct') {
        await api.unassignSSIDFromDevice(action.profileId, serial);
        enqueueSnackbar(`SSID "${action.ssidName}" removed from device`, { variant: 'success' });
      } else if (action.type === 'group') {
        const res = await api.updateGroup(action.groupId, { remove_devices: [serial] });
        enqueueSnackbar(
          `Device removed from group "${action.groupName}" — SSID "${action.ssidName}" will be stripped`,
          { variant: 'success' },
        );
        if (res?.ssid_strip_results || res?.radio_strip_results) {
          const parts = [];
          if (res.ssid_strip_results) parts.push('SSID settings updated');
          if (res.radio_strip_results) parts.push('radio profile settings updated');
          if (parts.length > 0) {
            enqueueSnackbar(parts.join(' · '), { variant: 'info' });
          }
        }
      }
      setSsidClearedSerials((prev) => new Set(prev).add(serial));
      setPublicWifiMap((prev) => {
        if (!prev[serial]) return prev;
        const next = { ...prev };
        delete next[serial];
        return next;
      });
      setRemoveSsidDialogOpen(false);
      setRemoveSsidTarget(null);
      setSelectedMapDevice(null);
      fetchPublicWifi();
      fetchSsidProfilesAndGroups();
      if (onRefresh) {
        onRefresh({ force: true }).catch((err) => {
          console.error('Failed to refresh devices after SSID removal', err);
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to remove SSID', { variant: 'error' });
    } finally {
      setRemoveSsidSaving(false);
    }
  }, [
    removeSsidTarget,
    removeSsidSaving,
    enqueueSnackbar,
    fetchPublicWifi,
    fetchSsidProfilesAndGroups,
    onRefresh,
  ]);

  const mapLegendRows = useMemo(() => {
    const rows = [];
    let showCap = false;
    let showPublicWifi = false;
    let showExcludedX = false;

    for (const device of mapRrmDevices) {
      if (isCapDevice(device)) {
        showCap = true;
        continue;
      }
      const serial = device.serial_number;
      const s = (serial || '').toLowerCase();
      if (publicWifiAnySerials.has(s)) {
        showPublicWifi = true;
        continue;
      }
      const ex = getOverviewExcludedMarkerStyle(serial, rrmPlanIndex.excludedSerialSet, publicWifiAnySerials);
      if (ex) {
        showExcludedX = true;
      }
    }

    if (showCap) {
      rows.push({ id: 'cap', color: '#7b1fa2', label: 'C', text: 'CAP (always shown)' });
    }
    if (showPublicWifi) {
      rows.push({ id: 'pw-w', color: MAP_UI.rrmPlanMarker, label: 'W', text: 'Public WiFi enabled' });
    }
    if (showExcludedX) {
      rows.push({ id: 'excl-x', color: MAP_UI.deviceOffline, label: 'X', labelSx: { fontSize: '0.6rem' }, text: 'Excluded critical parent · no Public WiFi' });
    }
    return rows;
  }, [mapRrmDevices, rrmPlanIndex, publicWifiAnySerials]);

  const mapBounds = useMemo(() => {
    if (mapRrmDevices.length === 0) return null;
    const latitudes = mapRrmDevices.map((d) => d.lat);
    const longitudes = mapRrmDevices.map((d) => d.lng);
    return {
      north: Math.max(...latitudes),
      south: Math.min(...latitudes),
      east: Math.max(...longitudes),
      west: Math.min(...longitudes),
    };
  }, [mapRrmDevices]);

  const computedMapCenter = useMemo(() => {
    if (mapRrmDevices.length === 0) return defaultCenter;
    const avgLat = mapRrmDevices.reduce((sum, d) => sum + d.lat, 0) / mapRrmDevices.length;
    const avgLng = mapRrmDevices.reduce((sum, d) => sum + d.lng, 0) / mapRrmDevices.length;
    return { lat: avgLat, lng: avgLng };
  }, [mapRrmDevices]);

  const computedMapZoom = useMemo(() => {
    if (!mapBounds) return 12;
    const latSpread = mapBounds.north - mapBounds.south;
    const lngSpread = mapBounds.east - mapBounds.west;
    const maxSpread = Math.max(latSpread, lngSpread);
    if (maxSpread > 1) return 8;
    if (maxSpread > 0.5) return 10;
    if (maxSpread > 0.1) return 12;
    if (maxSpread > 0.01) return 14;
    return 16;
  }, [mapBounds]);

  const syncMapViewportRef = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (!center || zoom == null) return;
    mapViewportRef.current = {
      center: { lat: center.lat(), lng: center.lng() },
      zoom,
    };
  }, []);

  const fitMapToDevices = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps || mapRrmDevices.length === 0) return;
    isFittingMapRef.current = true;
    const bounds = new window.google.maps.LatLngBounds();
    mapRrmDevices.forEach((device) => {
      bounds.extend({ lat: device.lat, lng: device.lng });
    });
    map.fitBounds(bounds);
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      syncMapViewportRef();
      setViewportLocked(true);
      isFittingMapRef.current = false;
    });
  }, [mapRrmDevices, syncMapViewportRef]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (mapRrmDevices.length > 0 && !hasFittedToDevicesRef.current) {
      fitMapToDevices();
      hasFittedToDevicesRef.current = true;
    } else if (viewportLocked) {
      map.setCenter(mapViewportRef.current.center);
      map.setZoom(mapViewportRef.current.zoom);
    } else if (mapRrmDevices.length > 0) {
      map.setCenter(computedMapCenter);
      map.setZoom(computedMapZoom);
    } else {
      map.setCenter(mapViewportRef.current.center);
      map.setZoom(mapViewportRef.current.zoom);
    }
  }, [
    mapRrmDevices.length,
    fitMapToDevices,
    viewportLocked,
    computedMapCenter,
    computedMapZoom,
  ]);

  // Fit once when markers first appear after map mount; do not refit when markers are added/removed later.
  useEffect(() => {
    if (!isActive || !isLoaded || mapRrmDevices.length === 0 || hasFittedToDevicesRef.current) return;
    fitMapToDevices();
    hasFittedToDevicesRef.current = true;
  }, [isActive, isLoaded, mapRrmDevices.length, fitMapToDevices]);

  useEffect(() => {
    hadMapDevicesRef.current = mapRrmDevices.length > 0;
  }, [mapRrmDevices.length]);

  // Re-fit to lamp GPS when returning to this tab; preserve viewport while tab stays open.
  useEffect(() => {
    if (isActive) return;
    setViewportLocked(false);
    hasFittedToDevicesRef.current = false;
  }, [isActive]);

  const handleMapUserViewportChange = useCallback(() => {
    if (isFittingMapRef.current) return;
    syncMapViewportRef();
    setViewportLocked(true);
  }, [syncMapViewportRef]);

  useEffect(() => {
    const next = new Map();
    mapRrmDevices.forEach((d) => {
      if (d.serial_number) next.set(d.serial_number, d);
    });
    mapDeviceBySerialRef.current = next;
  }, [mapRrmDevices]);

  markerClickStateRef.current.drawingMode = drawingMode;

  const handleMarkerClick = useCallback((serial) => {
    if (markerClickStateRef.current.drawingMode) return;
    const device = mapDeviceBySerialRef.current.get(serial);
    if (device) setSelectedMapDevice(device);
  }, []);

  const getMarkerContent = useCallback((device) => {
    const serial = device.serial_number || '';
    if (isCapDevice(device)) {
      const capKey = `${serial}:cap`;
      if (!markerContentCacheRef.current.has(capKey)) {
        markerContentCacheRef.current.set(capKey, createRrmMarkerDiv('#7b1fa2', 'C'));
      }
      return markerContentCacheRef.current.get(capKey);
    }
    const ex = getOverviewExcludedMarkerStyle(serial, rrmPlanIndex.excludedSerialSet, publicWifiAnySerials);
    const st = getOverviewMapMarkerStyle(serial, publicWifiAnySerials);
    const cacheKey = `${serial}:${ex ? `${ex.color}:${ex.label}` : ''}:${st.color}:${st.label}`;
    if (!markerContentCacheRef.current.has(cacheKey)) {
      if (ex) {
        markerContentCacheRef.current.set(cacheKey, createRrmMarkerDiv(ex.color, ex.label));
      } else {
        markerContentCacheRef.current.set(cacheKey, createRrmMarkerDiv(st.color, st.label));
      }
    }
    return markerContentCacheRef.current.get(cacheKey);
  }, [rrmPlanIndex.excludedSerialSet, publicWifiAnySerials]);

  const getMarkerClickHandler = useCallback((serial) => {
    if (!markerClickBySerialRef.current.has(serial)) {
      markerClickBySerialRef.current.set(serial, () => handleMarkerClick(serial));
    }
    return markerClickBySerialRef.current.get(serial);
  }, [handleMarkerClick]);

  // Apply filters
  // NOTE: devicesWithPublicWifi already contains only Node devices with valid Public WiFi
  // Device type filter is redundant but kept for UI consistency
  const filteredDevices = useMemo(() => {
    return devicesWithPublicWifi.filter(device => {
      // Device type filter (should only show Nodes, but allow filtering for UI consistency)
      if (deviceTypeFilter !== 'all') {
        const deviceType = getDeviceType(device);
        if (deviceTypeFilter === 'node' && deviceType !== 'Node') return false;
        if (deviceTypeFilter === 'cap' && deviceType !== 'CAP') return false;
        if (deviceTypeFilter === 'ai' && deviceType !== 'AI') return false;
      }
      
      // Status filter (active = has clients, idle = no clients)
      // Offline devices can appear but will show 0 clients
      const clientCount = getConnectedClients(device);
      if (statusFilter === 'active' && clientCount === 0) return false;
      if (statusFilter === 'idle' && clientCount > 0) return false;
      
      return true;
    });
  }, [devicesWithPublicWifi, deviceTypeFilter, statusFilter, getConnectedClients, getDeviceType]);

  const handleSort = useCallback((col) => {
    setSortDir(prev => sortCol === col && prev === 'asc' ? 'desc' : 'asc');
    setSortCol(col);
  }, [sortCol]);

  const sortedDevices = useMemo(() => {
    if (!sortCol) return filteredDevices;
    return [...filteredDevices].sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'device': av = a.serial_number || ''; bv = b.serial_number || ''; break;
        case 'type': av = getDeviceType(a); bv = getDeviceType(b); break;
        case 'ssid': av = getSSID(a) || ''; bv = getSSID(b) || ''; break;
        case 'clients': av = getConnectedClients(a); bv = getConnectedClients(b); break;
        case 'status': av = a.connected ? 1 : 0; bv = b.connected ? 1 : 0; break;
        // Use worst of parent vs CAP medians so rows with only to_cap (common on nodes) still sort.
        case 'latency': av = getWorstLatencyMs(a) ?? Infinity; bv = getWorstLatencyMs(b) ?? Infinity; break;
        case 'txfailed': av = getTxFailed(a) ?? Infinity; bv = getTxFailed(b) ?? Infinity; break;
        default: return 0;
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [filteredDevices, sortCol, sortDir, getDeviceType, getSSID, getConnectedClients, getTxFailed]);

  useEffect(() => { setPage(0); }, [sortCol, sortDir, deviceTypeFilter, statusFilter]);

  const paginatedDevices = useMemo(
    () => sortedDevices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedDevices, page, rowsPerPage]
  );

  // Calculate summary stats (only devices with valid SSID)
  // CRITICAL: Aggregations MUST exclude offline devices
  const stats = useMemo(() => {
    const totalDevices = devicesWithPublicWifi.length;
    const onlineDevices = devicesWithPublicWifi.filter(device => device.connected).length;
    
    // Only count online devices in Public WiFi aggregations
    // Offline devices cannot have clients and must not contribute to metrics
    const onlineDevicesOnly = devicesWithPublicWifi.filter(device => device.connected);
    const devicesWithClients = onlineDevicesOnly.filter(device => getConnectedClients(device) > 0).length;
    const totalClients = onlineDevicesOnly.reduce((sum, device) => sum + getConnectedClients(device), 0);
    
    return { totalDevices, onlineDevices, devicesWithClients, totalClients };
  }, [devicesWithPublicWifi, getConnectedClients]);

  // Get device icon
  const getDeviceIcon = (device) => {
    const deviceType = getDeviceType(device);
    const color = device.connected ? MAP_UI.deviceOnlineBorder : '#9e9e9e';
    
    if (deviceType === 'CAP') {
      return <RouterIcon sx={{ fontSize: 20, color }} />;
    }
    return <DevicesIcon sx={{ fontSize: 20, color }} />;
  };

  const goToDevice = useCallback((serial) => {
    if (!serial) return;
    navigate(`/device/${serial}`);
  }, [navigate]);

  // Row click toggles drilldown only (no navigation)
  const toggleRowExpanded = useCallback((serial) => {
    if (!serial) return;
    setExpandedRows((prev) => ({ ...prev, [serial]: !prev?.[serial] }));
  }, []);

  const isMissingClientValue = useCallback((value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return true;
      if (trimmed.toLowerCase() === 'n/a') return true;
    }
    return false;
  }, []);

  const displayClientValue = (value) => (isMissingClientValue(value) ? '—' : value);
  const displayClientMac = (value) => (isMissingClientValue(value) ? 'Hidden' : value);

  const applyLiveMacSearch = useCallback(() => {
    const v = liveMacInput.trim();
    if (!v) {
      setLiveMacError('');
      setLiveMacQuery('');
      return;
    }
    if (!looksLikeClientMac(v)) {
      setLiveMacError('Enter a full client MAC (aa:bb:cc:dd:ee:ff).');
      return;
    }
    setLiveMacError('');
    setLiveMacQuery(v);
  }, [liveMacInput]);

  // Returns ONLY station clients from the dedicated public-wifi endpoint
  // CRITICAL: Offline devices CANNOT have connected clients - return empty array
  const getPublicWifiClients = useCallback((device) => {
    if (!device?.connected) return [];
    const serial = (device?.serial_number || '').toLowerCase();
    const entry = publicWifiMap[serial];
    if (!entry?.interfaces) return [];

    const clients = [];
    entry.interfaces.forEach((iface) => {
      if (iface.mode !== 'ap') return;
      (iface.associations || []).forEach((assoc) => {
        clients.push({
          station: assoc?.station || '',
          matched: !!assoc?.matched,
          rssi: assoc?.rssi ?? null,
          rx_bytes: assoc?.rx_bytes ?? null,
          tx_bytes: assoc?.tx_bytes ?? null,
          connected_time: assoc?.connected_time ?? null,
        });
      });
    });
    return clients;
  }, [publicWifiMap]);

  const associatedClientRows = useMemo(() => {
    const rows = [];
    for (const device of devicesWithPublicWifi) {
      if (!device.connected) continue;
      const serialLower = (device.serial_number || '').toLowerCase();
      const entry = publicWifiMap[serialLower];
      if (!entry?.interfaces) continue;
      const baseSerial = device.serial_number;
      const baseName = device.name || device.serial_number;
      entry.interfaces.forEach((iface) => {
        if ((iface.mode || '').toLowerCase() !== 'ap') return;
        const ssid = iface.ssid || '—';
        (iface.associations || []).forEach((assoc, idx) => {
          const station = assoc?.station || '';
          const searching = looksLikeClientMac(liveMacQuery);
          if (searching && !assoc?.matched && !station) return;
          rows.push({
            key: `${serialLower}:${iface.name || 'if'}:${station || idx}`,
            clientMac: station,
            baseSerial,
            baseName,
            ssid,
            rssi: assoc?.rssi ?? null,
            rx_bytes: assoc?.rx_bytes ?? null,
            tx_bytes: assoc?.tx_bytes ?? null,
            connected_time: assoc?.connected_time ?? null,
          });
        });
      });
    }
    return rows;
  }, [devicesWithPublicWifi, publicWifiMap, liveMacQuery]);

  const handleClientSort = useCallback((col) => {
    setClientSortDir((prev) => (clientSortCol === col && prev === 'asc' ? 'desc' : 'asc'));
    setClientSortCol(col);
  }, [clientSortCol]);

  const sortedAssociatedClients = useMemo(() => {
    const dir = clientSortDir === 'asc' ? 1 : -1;
    return [...associatedClientRows].sort((a, b) => {
      let cmp = 0;
      switch (clientSortCol) {
        case 'clientMac':
          cmp = (a.clientMac || '').toLowerCase().localeCompare((b.clientMac || '').toLowerCase());
          break;
        case 'base':
          cmp = (a.baseName || a.baseSerial || '').toLowerCase().localeCompare((b.baseName || b.baseSerial || '').toLowerCase());
          break;
        case 'ssid':
          cmp = (a.ssid || '').toLowerCase().localeCompare((b.ssid || '').toLowerCase());
          break;
        case 'rssi': {
          const na = parseRssiNum(a.rssi);
          const nb = parseRssiNum(b.rssi);
          cmp = (na ?? -9999) - (nb ?? -9999);
          break;
        }
        case 'rx_bytes':
          cmp = (Number(a.rx_bytes) || 0) - (Number(b.rx_bytes) || 0);
          break;
        case 'tx_bytes':
          cmp = (Number(a.tx_bytes) || 0) - (Number(b.tx_bytes) || 0);
          break;
        case 'connected_time':
          cmp = (Number(a.connected_time) || 0) - (Number(b.connected_time) || 0);
          break;
        default:
          return 0;
      }
      return dir * cmp;
    });
  }, [associatedClientRows, clientSortCol, clientSortDir]);

  const paginatedAssociatedClients = useMemo(
    () => sortedAssociatedClients.slice(
      clientPage * clientRowsPerPage,
      clientPage * clientRowsPerPage + clientRowsPerPage
    ),
    [sortedAssociatedClients, clientPage, clientRowsPerPage]
  );

  const histTableRows = useMemo(() => (histRows || []).map((r) => {
    const serialLower = (r.serial || '').toLowerCase();
    const stationKey = (r.stationRaw || r.station || '').toLowerCase();
    return {
      ...r,
      key: `${serialLower}|${stationKey}`,
      clientMac: r.station,
      baseSerial: r.serial,
      baseName: deviceNameBySerial[serialLower] || r.serial,
    };
  }), [histRows, deviceNameBySerial]);

  const handleHistSort = useCallback((col) => {
    setHistSortDir((prev) => (histSortCol === col && prev === 'asc' ? 'desc' : 'asc'));
    setHistSortCol(col);
  }, [histSortCol]);

  const sortedHistTableRows = useMemo(() => {
    const dir = histSortDir === 'asc' ? 1 : -1;
    return [...histTableRows].sort((a, b) => {
      let cmp = 0;
      switch (histSortCol) {
        case 'reported_at':
          cmp = new Date(a.reported_at).getTime() - new Date(b.reported_at).getTime();
          break;
        case 'clientMac':
          cmp = (a.clientMac || '').toLowerCase().localeCompare((b.clientMac || '').toLowerCase());
          break;
        case 'base':
          cmp = (a.baseName || a.baseSerial || '').toLowerCase().localeCompare((b.baseName || b.baseSerial || '').toLowerCase());
          break;
        case 'ssid':
          cmp = (a.ssid || '').toLowerCase().localeCompare((b.ssid || '').toLowerCase());
          break;
        case 'rssi': {
          const na = parseRssiNum(a.rssi);
          const nb = parseRssiNum(b.rssi);
          cmp = (na ?? -9999) - (nb ?? -9999);
          break;
        }
        case 'rx_bytes':
          cmp = (Number(a.rx_bytes) || 0) - (Number(b.rx_bytes) || 0);
          break;
        case 'tx_bytes':
          cmp = (Number(a.tx_bytes) || 0) - (Number(b.tx_bytes) || 0);
          break;
        case 'tx_failed':
          cmp = (Number(a.tx_failed) || 0) - (Number(b.tx_failed) || 0);
          break;
        case 'connected_time':
          cmp = (Number(a.connected_time) || 0) - (Number(b.connected_time) || 0);
          break;
        default:
          return 0;
      }
      return dir * cmp;
    });
  }, [histTableRows, histSortCol, histSortDir]);

  const histResultsTruncated = histSnapshotCount != null
    && histHasQueried
    && histSnapshotCount > ASSOC_HISTORY_MAX_ROWS;

  const handleExportHistCsv = useCallback(() => {
    if (!sortedHistTableRows.length) return;
    const headerRow = [
      'last_seen_utc',
      'connected_time_sec',
      'station_mac',
      'lamp_name',
      'lamp_serial',
      'ap_interface',
      'band',
      'ssid',
      'bssid',
      'rssi',
      'rx_bytes',
      'tx_bytes',
      'tx_failed',
      'rx_rate',
      'tx_rate',
    ];
    const dataRows = sortedHistTableRows.map((row) => [
      row.reported_at,
      row.connected_time,
      row.clientMac ?? row.station,
      row.baseName,
      row.baseSerial,
      row.interface,
      row.band,
      row.ssid,
      row.bssid,
      row.rssi,
      row.rx_bytes,
      row.tx_bytes,
      row.tx_failed,
      formatWifiRateDisplay(row.rx_rate),
      formatWifiRateDisplay(row.tx_rate),
    ]);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(`public-wifi-associated-clients-history-${stamp}.csv`, headerRow, dataRows);
  }, [sortedHistTableRows]);

  useEffect(() => {
    setClientPage(0);
  }, [overviewTab, clientSortCol, clientSortDir]);

  const handleRefresh = useCallback(() => {
    fetchPublicWifi();
  }, [fetchPublicWifi]);

  if (publicWifiLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body1" color="text.secondary">
          Loading Public WiFi data…
        </Typography>
      </Box>
    );
  }

  if (!devices || !Array.isArray(devices) || devices.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No devices available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={publicWifiStatSx.card}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <WifiIcon sx={publicWifiStatSx.icon} />
              </Box>
              <Typography variant="h4" sx={publicWifiStatSx.value}>
                {stats.totalDevices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Devices with Public WiFi
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={publicWifiStatSx.card}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <PowerIcon sx={publicWifiStatSx.icon} />
              </Box>
              <Typography variant="h4" sx={publicWifiStatSx.value}>
                {stats.onlineDevices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Online Public WiFi Devices
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={publicWifiStatSx.card}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <PeopleIcon sx={publicWifiStatSx.icon} />
              </Box>
              <Typography variant="h4" sx={publicWifiStatSx.value}>
                {stats.devicesWithClients}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Devices with Clients (&gt;0)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={publicWifiStatSx.card}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <PeopleIcon sx={publicWifiStatSx.icon} />
              </Box>
              <Typography variant="h4" sx={publicWifiStatSx.value}>
                {stats.totalClients}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Connected Clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={2} sx={{ mb: 2 }}>
        <Tabs
          value={overviewTab}
          onChange={(_, v) => setOverviewTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Public WiFi overview"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          <Tab label="Public WiFi Device Locations" />
          <Tab label="Public WiFi Devices" />
          <Tab label="Associated clients" />
          <Tab label="Associated clients history" />
          <Tab icon={<ExploreIcon sx={{ fontSize: '1.1rem !important' }} />} iconPosition="start" label="Field Survey" />
        </Tabs>
      </Paper>

      {overviewTab === 0 && (
      <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary">
              {mapDeviceFilters.size === 0
                ? 'No layers selected — use Layers to choose what to plot on the map.'
                : mapRrmDevices.length > 0
                  ? formatMapDevicesBanner(mapRrmDevices, mapDeviceFilters, rrmPlanIndex.excludedSerialSet, publicWifiAnySerials)
                  : 'No devices match the selected layers (check GPS and Public WiFi / RRM excluded data).'}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', width: '100%', height: { xs: '400px', md: '650px' } }}>
            {loadError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Alert severity="info" sx={{ m: 2 }}>
                  Map unavailable. Device list and metrics remain functional.
                </Alert>
              </Box>
            ) : !isLoaded ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                display: 'flex',
                gap: 1,
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LayersIcon sx={{ fontSize: '1rem !important' }} />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setShowDevicesMenuAnchor(e.currentTarget)}
                  sx={{
                    backgroundColor: mapDeviceFilters.size > 0 ? MAP_UI.accentTint : 'rgba(255,255,255,0.95)',
                    borderColor: MAP_UI.accent,
                    color: MAP_UI.accentDark,
                    '&:hover': { backgroundColor: MAP_UI.accentTintHover },
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                  }}
                >
                  Layers ({mapDeviceFilters.size})
                </Button>
                <Menu
                  anchorEl={showDevicesMenuAnchor}
                  open={Boolean(showDevicesMenuAnchor)}
                  onClose={() => setShowDevicesMenuAnchor(null)}
                >
                  {MAP_DEVICE_FILTER_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.id}
                      dense
                      selected={mapDeviceFilters.has(opt.id)}
                      onClick={() => toggleMapDeviceFilter(opt.id)}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: MAP_UI.accentTint,
                          '&:hover': { bgcolor: MAP_UI.accentTintHover },
                        },
                      }}
                    >
                      <ListItemText primary={opt.label} secondary={opt.detail} />
                    </MenuItem>
                  ))}
                </Menu>
                <Button
                  variant={drawingMode ? 'contained' : 'outlined'}
                  size="small"
                  onClick={toggleDrawingMode}
                  sx={{
                    backgroundColor: drawingMode ? MAP_UI.rrmPlanMarkerActive : 'rgba(255,255,255,0.95)',
                    borderColor: MAP_UI.rrmPlanMarkerActive,
                    color: drawingMode ? 'white' : MAP_UI.rrmPlanMarkerActive,
                    '&:hover': {
                      backgroundColor: drawingMode ? MAP_UI.rrmPlanMarker : 'rgba(236, 239, 241, 0.95)',
                    },
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                  }}
                >
                  {drawingMode ? 'Cancel' : 'Select Devices'}
                </Button>
                <Button
                  variant={showConnections ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<AccountTreeIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={() => setShowConnections((v) => !v)}
                  sx={{
                    backgroundColor: showConnections ? MAP_UI.accent : 'rgba(255,255,255,0.95)',
                    borderColor: MAP_UI.accent,
                    color: showConnections ? 'white' : MAP_UI.accent,
                    '&:hover': { backgroundColor: showConnections ? MAP_UI.accentDark : MAP_UI.accentTintHover },
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                  }}
                >
                  {showConnections ? 'Hide Connections' : 'Show Connections'}
                </Button>
              </Box>
              {drawingMode && (
                <Box sx={{
                  position: 'absolute',
                  top: 60,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  backgroundColor: MAP_UI.accentBg,
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  fontSize: '13px',
                  pointerEvents: 'none',
                }}>
                  Click to add points, click near start point to close polygon
                </Box>
              )}
              <GoogleMap
                key={`public-wifi-overview-map-${theme.palette.mode}`}
                mapContainerStyle={{
                  width: '100%',
                  height: '100%',
                }}
                {...(!viewportLocked
                  ? {
                    center: mapRrmDevices.length > 0 ? computedMapCenter : defaultCenter,
                    zoom: mapRrmDevices.length > 0 ? computedMapZoom : 12,
                  }
                  : {})}
                onLoad={handleMapLoad}
                onDragEnd={handleMapUserViewportChange}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                  draggable: !drawingMode,
                  gestureHandling: drawingMode ? 'none' : 'auto',
                  draggableCursor: drawingMode ? 'crosshair' : 'grab',
                  disableDoubleClickZoom: drawingMode,
                  mapId: 'PUBLIC_WIFI_OVERVIEW_MAP',
                  colorScheme: mapColorScheme,
                }}
              >
                {mapRrmDevices.map((device) => (
                  <AdvancedMarker
                    key={device.serial_number}
                    position={{ lat: device.lat, lng: device.lng }}
                    content={getMarkerContent(device)}
                    onClick={getMarkerClickHandler(device.serial_number)}
                    title={device.name || device.serial_number}
                    zIndex={1}
                  />
                ))}

                {polygons.map((poly) => (
                  <Polygon
                    key={poly.id}
                    paths={poly.coordinates}
                    options={{
                      fillColor: MAP_UI.rrmPlanPolygonFill,
                      fillOpacity: 0.25,
                      strokeWeight: 2,
                      strokeColor: MAP_UI.rrmPlanPolygonStroke,
                      clickable: false,
                    }}
                  />
                ))}

                {showConnections && connectionLines.map((conn) => (
                  <React.Fragment key={conn.key}>
                    <Polyline
                      path={[
                        { lat: conn.from.lat, lng: conn.from.lng },
                        { lat: conn.to.lat, lng: conn.to.lng },
                      ]}
                      options={{
                        strokeColor: conn.color,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        icons: [{
                          icon: {
                            path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                            scale: 3,
                            strokeColor: conn.color,
                            fillColor: conn.color,
                            fillOpacity: 1,
                          },
                          offset: '50%',
                        }],
                      }}
                    />
                    <OverlayView
                      position={{ lat: conn.midLat, lng: conn.midLng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Tooltip
                        title={conn.rssi != null ? `RSSI: ${conn.rssi} dBm · Distance: ${conn.distText}` : `Distance: ${conn.distText}`}
                        placement="top"
                        arrow
                        enterDelay={200}
                        leaveDelay={0}
                      >
                        <Box sx={{
                          position: 'relative',
                          transform: 'translate(-50%, calc(-50% - 24px))',
                          backgroundColor: conn.color,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '11px',
                          borderRadius: '3px',
                          padding: '3px 7px',
                          textAlign: 'center',
                          minWidth: '50px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          pointerEvents: 'auto',
                          cursor: 'help',
                          lineHeight: 1,
                        }}
                        >
                          {conn.label}
                        </Box>
                      </Tooltip>
                    </OverlayView>
                  </React.Fragment>
                ))}

                {selectedMapDevice && !drawingMode && (
                  <InfoWindow
                    position={{ lat: selectedMapDevice.lat, lng: selectedMapDevice.lng }}
                    onCloseClick={() => setSelectedMapDevice(null)}
                  >
                    <Box sx={{ p: 1, minWidth: 200 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedMapDevice.name || selectedMapDevice.serial_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {selectedMapDevice.serial_number}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        SSID: {getSSID(selectedMapDevice) || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Chip
                          label={selectedMapDevice.connected ? 'Online' : 'Offline'}
                          size="small"
                          color={selectedMapDevice.connected ? 'success' : 'default'}
                          variant={selectedMapDevice.connected ? 'filled' : 'outlined'}
                        />
                      </Box>
                      <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                        Clients: {getConnectedClients(selectedMapDevice)}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {canMutate && getSSID(selectedMapDevice) && (
                          <Box
                            component="button"
                            onClick={() => handleOpenRemoveSsidDialog(selectedMapDevice)}
                            sx={{
                              ...publicWifiMapActionBtnSx,
                              mt: 0,
                              bgcolor: 'transparent',
                              color: MAP_UI.deviceOfflineBorder,
                              borderColor: MAP_UI.deviceOfflineBorder,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                              '&:hover': { bgcolor: MAP_UI.offlineChipBg },
                            }}
                          >
                            <WifiOffIcon sx={{ fontSize: '0.9rem' }} />
                            Remove SSID
                          </Box>
                        )}
                        <Box
                          component="button"
                          onClick={() => {
                            setSelectedMapDevice(null);
                            navigate(`/device/${selectedMapDevice.serial_number}`);
                          }}
                          sx={{ ...publicWifiMapActionBtnSx, mt: 0 }}
                        >
                          View Device
                        </Box>
                      </Box>
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
              {mapDeviceFilters.size === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 3,
                    maxWidth: 320,
                    pointerEvents: 'none',
                  }}
                >
                  <Alert severity="info" sx={{ boxShadow: 2 }}>
                    Select at least one layer to plot markers on the map (CAPs appear whenever any layer is on).
                  </Alert>
                </Box>
              )}
              {mapLegendRows.length > 0 && (
                <Box
                  component="aside"
                  aria-label="Map marker legend"
                  sx={{
                    position: 'absolute',
                    top: { xs: 8, sm: 12 },
                    left: { xs: 8, sm: 12 },
                    zIndex: 2,
                    maxWidth: { xs: 260, sm: 288 },
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.94)'),
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, letterSpacing: 0.2 }}>
                    Marker legend
                  </Typography>
                  {mapLegendRows.map((row, idx, arr) => (
                    <Box
                      key={row.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: idx < arr.length - 1 ? 0.6 : 0,
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          bgcolor: row.color,
                          border: '2px solid #fff',
                          color: '#fff',
                          fontSize: row.labelSx?.fontSize || '0.65rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Arial, sans-serif',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                          lineHeight: 1,
                          letterSpacing: row.labelSx?.letterSpacing,
                        }}
                      >
                        {row.label}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, pt: 0.15 }}>
                        {row.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              </>
            )}
          </Box>
        </Paper>
      )}

      {overviewTab === 1 && (
      <>
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Device Type</InputLabel>
              <Select
                value={deviceTypeFilter}
                label="Device Type"
                onChange={(e) => setDeviceTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="node">Nodes</MenuItem>
                <MenuItem value="cap">CAPs</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Devices</MenuItem>
                <MenuItem value="active">With Clients (&gt; 0)</MenuItem>
                <MenuItem value="idle">No Clients (0)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh Public WiFi data">
              <span>
                <IconButton onClick={handleRefresh} color="primary" disabled={publicWifiLoading}>
                  {publicWifiLoading
                    ? <CircularProgress size={20} color="inherit" />
                    : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Devices Table */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{
                    py: 1.5,
                    verticalAlign: 'top',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Public WiFi Devices
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {filteredDevices.length} device(s) actively broadcasting Public WiFi (with SSID)
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={4}
                  align="left"
                  sx={{
                    py: 1.5,
                    verticalAlign: 'top',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderLeft: '2px solid rgba(0, 0, 0, 0.1)',
                    borderRight: '2px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <AccountTreeIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Backhaul Health
                    </Typography>
                    <Tooltip
                      title="These metrics describe the backhaul link quality between the device and its parent (CAP). They do NOT represent Public WiFi client performance."
                      arrow
                      placement="top"
                    >
                      <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortCol === 'device'} direction={sortCol === 'device' ? sortDir : 'asc'} onClick={() => handleSort('device')}>Device</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortCol === 'type'} direction={sortCol === 'type' ? sortDir : 'asc'} onClick={() => handleSort('type')}>Type</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortCol === 'ssid'} direction={sortCol === 'ssid' ? sortDir : 'asc'} onClick={() => handleSort('ssid')}>SSID</TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortCol === 'clients'} direction={sortCol === 'clients' ? sortDir : 'asc'} onClick={() => handleSort('clients')}>Clients</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortCol === 'status'} direction={sortCol === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>Device Status</TableSortLabel>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderLeft: '2px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <TableSortLabel active={sortCol === 'latency'} direction={sortCol === 'latency' ? sortDir : 'asc'} onClick={() => handleSort('latency')}>Latency (ms)</TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.85rem', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                  <TableSortLabel active={sortCol === 'txfailed'} direction={sortCol === 'txfailed' ? sortDir : 'asc'} onClick={() => handleSort('txfailed')}>TX Failed (%)</TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.85rem', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                  STA → AP RSSI (dBm)
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderRight: '2px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  AP → STA RSSI (dBm)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDevices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {devicesWithPublicWifi.length === 0
                        ? 'No devices are actively broadcasting Public WiFi (no valid SSID found)'
                        : 'No devices match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedDevices.map((device) => {
                  const clientCount = getConnectedClients(device);
                  const deviceType = getDeviceType(device);
                  const ssid = getSSID(device);
                  const isOnline = device.connected;
                  const parentLatencyMs = getParentLatencyMs(device);
                  const capLatencyMs = getCapLatencyMs(device);
                  const txFailed = getTxFailed(device);
                  const isExpanded = !!expandedRows?.[device.serial_number];
                  const publicClients = getPublicWifiClients(device);
                  // CRITICAL: Offline devices cannot be expanded and have no clients
                  const canExpand = isOnline && publicClients.length > 0;

                  const parent =
                    device?.parent_serial && devices && Array.isArray(devices)
                      ? devices.find(
                          (d) =>
                            d.serial_number &&
                            d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
                        )
                      : null;
                  const connectionDetails = parent ? getUplinkDetails(device, parent) : null;
                  const staToApRssiRaw = connectionDetails?.rssi ?? null; // Device page labels this STA → AP RSSI
                  const apToStaRssiRaw = connectionDetails?.stationRssi ?? null;

                  const normalizeRssi = (value) => {
                    if (value === undefined || value === null || value === 'N/A') return null;
                    const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
                    if (!Number.isFinite(parsed)) return null;
                    // RSSI should never be 0 in real data; treat as missing for parity with Device page truthy checks
                    if (parsed === 0) return null;
                    return parsed;
                  };

                  const staToApRssi = normalizeRssi(staToApRssiRaw);
                  const apToStaRssi = normalizeRssi(apToStaRssiRaw);

                  // Color helper for latency cell — wraps the shared ms-based
                  // helper so we get a neutral grey when there is no value.
                  const getLatencyColor = (ms) => {
                    if (ms === null || ms === undefined) return '#9e9e9e';
                    return getLatencyHexColor(ms);
                  };

                  const getTxFailedColor = (value) => {
                    if (value === null) return '#9e9e9e';
                    if (value <= 0.1) return MAP_UI.throughputExcellent;
                    if (value <= 0.3) return MAP_UI.moderate;
                    return MAP_UI.throughputPoor;
                  };

                  const getRssiColor = (rssiValue) => {
                    if (rssiValue === null) return '#9e9e9e';
                    if (rssiValue >= -70) return MAP_UI.throughputExcellent;
                    if (rssiValue >= -80) return MAP_UI.moderate;
                    return MAP_UI.throughputPoor;
                  };

                  return (
                    <React.Fragment key={device.serial_number}>
                      <TableRow
                        hover
                        onClick={() => canExpand && toggleRowExpanded(device.serial_number)}
                        sx={{ cursor: canExpand ? 'pointer' : 'default' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: 24 }}>
                              {canExpand ? (
                                isExpanded ? (
                                  <KeyboardArrowDownIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                ) : (
                                  <KeyboardArrowRightIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                )
                              ) : null}
                            </Box>
                            {getDeviceIcon(device)}
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goToDevice(device.serial_number);
                                }}
                              >
                                {device.name || device.serial_number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {device.serial_number}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      <TableCell>
                        <Chip
                          label={deviceType}
                          size="small"
                          variant="outlined"
                          color={deviceType === 'CAP' ? 'secondary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {ssid || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: clientCount > 0 ? MAP_UI.publicWifiStat : '#9e9e9e',
                            fontSize: '1.1rem'
                          }}
                        >
                          {clientCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isOnline ? 'Online' : 'Offline'}
                          size="small"
                          color={isOnline ? 'success' : 'default'}
                          variant={isOnline ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)', borderLeft: '2px solid rgba(0, 0, 0, 0.1)' }}
                      >
                        {parentLatencyMs === null && capLatencyMs === null ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center' }}>
                            {parentLatencyMs !== null && (
                              <Tooltip title={formatLatencyTooltip(device.latency?.to_parent, 'Median to parent target')}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, fontFamily: 'monospace', color: getLatencyColor(parentLatencyMs), cursor: 'help' }}
                                >
                                  P {formatLatencyMs(parentLatencyMs)}
                                </Typography>
                              </Tooltip>
                            )}
                            {capLatencyMs !== null && (
                              <Tooltip title={formatLatencyTooltip(device.latency?.to_cap, 'Median to CAP target')}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, fontFamily: 'monospace', color: getLatencyColor(capLatencyMs), cursor: 'help' }}
                                >
                                  C {formatLatencyMs(capLatencyMs)}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                        {txFailed !== null ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: getTxFailedColor(txFailed)
                            }}
                          >
                            {(txFailed * 100).toFixed(1)}%
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                        {staToApRssi !== null ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: getRssiColor(staToApRssi)
                            }}
                          >
                            {staToApRssi}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell 
                        align="center"
                        sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)', borderRight: '2px solid rgba(0, 0, 0, 0.1)' }}
                      >
                        {apToStaRssi !== null ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: getRssiColor(apToStaRssi)
                            }}
                          >
                            {apToStaRssi}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      </TableRow>

                      {/* Drilldown row */}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
                              {!isOnline ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                  Device is offline – client data unavailable
                                </Typography>
                              ) : publicClients.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                  No connected Public WiFi clients
                                </Typography>
                              ) : (
                                <Table size="small" sx={{ backgroundColor: 'transparent' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Client MAC</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>RSSI (dBm)</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>RX Bytes</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>TX Bytes</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Connected Time</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {publicClients.map((c, idx) => (
                                      <TableRow key={`${device.serial_number}-client-${c.station || idx}`}>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>
                                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>
                                            {displayClientMac(c.station)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                          {displayClientValue(c.rssi)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                          {displayClientValue(c.rx_bytes)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                          {displayClientValue(c.tx_bytes)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                          {displayClientValue(c.connected_time)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedDevices.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>
      </>
      )}

      {overviewTab === 2 && (
      <>
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="body2" color="text.secondary">
              One row per station associated to a Public WiFi AP. Client MACs are hidden unless you search a full MAC. Only online access points are included.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search client MAC"
              placeholder="aa:bb:cc:dd:ee:ff"
              value={liveMacInput}
              error={Boolean(liveMacError)}
              helperText={liveMacError || (liveMacQuery ? `Showing matches for ${liveMacQuery}` : 'Full MAC required')}
              onChange={(e) => setLiveMacInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyLiveMacSearch(); }}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={applyLiveMacSearch}>Search</Button>
            {liveMacQuery ? (
              <Button
                size="small"
                onClick={() => { setLiveMacInput(''); setLiveMacQuery(''); setLiveMacError(''); }}
              >
                Clear
              </Button>
            ) : null}
            <Tooltip title="Refresh Public WiFi data">
              <span>
                <IconButton onClick={handleRefresh} color="primary" disabled={publicWifiLoading}>
                  {publicWifiLoading
                    ? <CircularProgress size={20} color="inherit" />
                    : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Associated clients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sortedAssociatedClients.length} association(s)
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'clientMac'}
                    direction={clientSortCol === 'clientMac' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('clientMac')}
                  >
                    Client MAC
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'base'}
                    direction={clientSortCol === 'base' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('base')}
                  >
                    Base device
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'ssid'}
                    direction={clientSortCol === 'ssid' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('ssid')}
                  >
                    SSID
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'rssi'}
                    direction={clientSortCol === 'rssi' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('rssi')}
                  >
                    RSSI (dBm)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'rx_bytes'}
                    direction={clientSortCol === 'rx_bytes' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('rx_bytes')}
                  >
                    RX bytes
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'tx_bytes'}
                    direction={clientSortCol === 'tx_bytes' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('tx_bytes')}
                  >
                    TX bytes
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={clientSortCol === 'connected_time'}
                    direction={clientSortCol === 'connected_time' ? clientSortDir : 'asc'}
                    onClick={() => handleClientSort('connected_time')}
                  >
                    Connected time
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAssociatedClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No associated Public WiFi clients (all access points idle or offline)
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAssociatedClients.map((row) => {
                  const rssiNum = parseRssiNum(row.rssi);
                  return (
                    <TableRow key={row.key} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {displayClientMac(row.clientMac)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => goToDevice(row.baseSerial)}
                        >
                          {row.baseName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.baseSerial}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {row.ssid}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            color: rssiNum === null ? 'text.secondary' : getRssiColorForClient(rssiNum),
                          }}
                        >
                          {displayClientValue(row.rssi)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {formatBytes(row.rx_bytes)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {formatBytes(row.tx_bytes)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {formatConnectedTime(row.connected_time)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sortedAssociatedClients.length}
          page={clientPage}
          onPageChange={(_, newPage) => setClientPage(newPage)}
          rowsPerPage={clientRowsPerPage}
          onRowsPerPageChange={(e) => {
            setClientRowsPerPage(parseInt(e.target.value, 10));
            setClientPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
      </>
      )}

      {overviewTab === 3 && (
      <>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          onClick={() => setHistFiltersOpen((o) => !o)}
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            userSelect: 'none',
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
            borderBottom: histFiltersOpen ? 1 : 0,
            borderColor: 'divider',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
            },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setHistFiltersOpen((o) => !o); }}
            aria-label={histFiltersOpen ? 'Collapse filters' : 'Expand filters'}
            sx={{
              transform: histFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
          <FilterListIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Search filters
          </Typography>
          {histActiveFilterCount > 0 && (
            <Chip
              size="small"
              label={`${histActiveFilterCount} active`}
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="small"
            onClick={(e) => { e.stopPropagation(); fetchHistHistory(); }}
            disabled={histLoading || (histPreset === 'custom' && !histRange)}
            startIcon={histLoading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            sx={{ minWidth: 108, boxShadow: 'none' }}
          >
            {histLoading ? 'Searching…' : 'Search'}
          </Button>
        </Box>

        <Collapse in={histFiltersOpen} timeout="auto">
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5, letterSpacing: 1.1, fontWeight: 600 }}
            >
              Time range
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="hist-range-label">Window</InputLabel>
                  <Select
                    labelId="hist-range-label"
                    label="Window"
                    value={histPreset}
                    onChange={(e) => setHistPreset(e.target.value)}
                  >
                    <MenuItem value="1h">Last 1 hour</MenuItem>
                    <MenuItem value="24h">Last 24 hours</MenuItem>
                    <MenuItem value="7d">Last 7 days</MenuItem>
                    <MenuItem value="30d">Last 30 days</MenuItem>
                    <MenuItem value="custom">Custom range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {histPreset === 'custom' && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="From"
                      value={histCustomFrom}
                      onChange={(e) => setHistCustomFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="To"
                      value={histCustomTo}
                      onChange={(e) => setHistCustomTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5, letterSpacing: 1.1, fontWeight: 600 }}
            >
              Lamp &amp; station
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Station MAC"
                  placeholder="aa:bb:cc:dd:ee:ff"
                  value={histStationFilter}
                  onChange={(e) => setHistStationFilter(e.target.value)}
                  helperText="Full client MAC. Hits show the typed address."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Lamp serial"
                  placeholder="Partial match"
                  value={histSerialFilter}
                  onChange={(e) => setHistSerialFilter(e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5, letterSpacing: 1.1, fontWeight: 600 }}
            >
              Traffic thresholds
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, letterSpacing: 0.8, fontWeight: 600, textTransform: 'uppercase' }}
            >
              Time
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Min connected time (sec)"
                  placeholder="≥ value"
                  value={histMinConnectedTime}
                  onChange={(e) => setHistMinConnectedTime(e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max connected time (sec)"
                  placeholder="≤ value"
                  value={histMaxConnectedTime}
                  onChange={(e) => setHistMaxConnectedTime(e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, mb: 1, letterSpacing: 0.8, fontWeight: 600, textTransform: 'uppercase' }}
            >
              Data
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="hist-data-unit-label">Unit</InputLabel>
                  <Select
                    labelId="hist-data-unit-label"
                    label="Unit"
                    value={histDataSizeUnit}
                    onChange={(e) => setHistDataSizeUnit(e.target.value)}
                  >
                    {HIST_DATA_SIZE_UNITS.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={`Min RX (${histDataSizeUnit})`}
                  placeholder="≥ value"
                  value={histMinRxBytes}
                  onChange={(e) => setHistMinRxBytes(e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={`Min TX (${histDataSizeUnit})`}
                  placeholder="≥ value"
                  value={histMinTxBytes}
                  onChange={(e) => setHistMinTxBytes(e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>

            {histPreset === 'custom' && !histRange && (
              <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
                Select a valid From and To range (To must be after From).
              </Alert>
            )}

            <Box
              sx={{
                mt: 2.5,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520 }}>
                One row per client station on public WiFi. Metrics reflect the latest snapshot in the window.
              </Typography>
              <Button size="small" color="inherit" onClick={clearHistFilters} disabled={histActiveFilterCount === 0}>
                Clear filters
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {histError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistError(null)}>
          {histError}
        </Alert>
      )}

      {histResultsTruncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing subset of clients
        </Alert>
      )}

      {(histHasQueried || histLoading) && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card elevation={2} sx={publicWifiStatSx.card}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <PeopleIcon sx={publicWifiStatSx.icon} />
                </Box>
                {histLoading && histSnapshotCount == null ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                    <CircularProgress size={28} sx={{ color: 'text.secondary' }} />
                  </Box>
                ) : (
                  <Typography variant="h4" sx={publicWifiStatSx.value}>
                    {histSnapshotCount != null ? histSnapshotCount.toLocaleString() : '—'}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Distinct clients in range
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Associated clients history
            </Typography>
            <Tooltip
              title="This table shows a sample of clients from the latest networking records in your time range, not every association. Use the Station MAC filter to look up a specific client."
              placement="top"
              arrow
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'inline-block', cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
              >
                {sortedHistTableRows.length} client sample row(s)
                {histLoading && histLoadPhase ? ` — ${histLoadPhase}` : ''}
              </Typography>
            </Tooltip>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleExportHistCsv}
            disabled={histLoading || sortedHistTableRows.length === 0}
          >
            Export to CSV
          </Button>
        </Box>
        <Box sx={{ position: 'relative' }}>
          {histLoading && histLoadProgress != null && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                bgcolor: (t) => alpha(t.palette.background.paper, 0.72),
                backdropFilter: 'blur(1px)',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={histLoadProgress}
                sx={{ width: '100%' }}
              />
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {histLoadPhase || 'Searching…'} ({Math.round(histLoadProgress)}%)
                </Typography>
              </Box>
            </Box>
          )}
          <TableContainer
            sx={{
              minHeight: histLoading ? 280 : undefined,
              opacity: histLoading ? 0.45 : 1,
              pointerEvents: histLoading ? 'none' : 'auto',
              transition: 'opacity 0.2s ease',
            }}
          >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'reported_at'}
                    direction={histSortCol === 'reported_at' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('reported_at')}
                  >
                    Last seen
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'connected_time'}
                    direction={histSortCol === 'connected_time' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('connected_time')}
                  >
                    Connected time
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'clientMac'}
                    direction={histSortCol === 'clientMac' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('clientMac')}
                  >
                    Station MAC
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'ssid'}
                    direction={histSortCol === 'ssid' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('ssid')}
                  >
                    SSID
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'rssi'}
                    direction={histSortCol === 'rssi' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('rssi')}
                  >
                    RSSI (dBm)
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'rx_bytes'}
                    direction={histSortCol === 'rx_bytes' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('rx_bytes')}
                  >
                    RX bytes
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'tx_bytes'}
                    direction={histSortCol === 'tx_bytes' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('tx_bytes')}
                  >
                    TX bytes
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'tx_failed'}
                    direction={histSortCol === 'tx_failed' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('tx_failed')}
                  >
                    TX failed
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>RX rate</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>TX rate</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={histSortCol === 'base'}
                    direction={histSortCol === 'base' ? histSortDir : 'asc'}
                    onClick={() => handleHistSort('base')}
                  >
                    Lamp serial
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHistTableRows.length === 0 && !histLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {!histHasQueried
                        ? 'Set filters and click Search to load associated clients history.'
                        : 'No rows match your search criteria.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : sortedHistTableRows.length === 0 && histLoading ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 8, border: 0 }} />
                </TableRow>
              ) : (
                sortedHistTableRows.map((row) => {
                  const rssiNum = parseRssiNum(row.rssi);
                  return (
                    <TableRow key={row.key} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        {formatSeenAt(row.reported_at)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {formatConnectedTime(row.connected_time)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {displayClientMac(row.clientMac)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {row.ssid || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            color: rssiNum === null ? 'text.secondary' : getRssiColorForClient(rssiNum),
                          }}
                        >
                          {displayClientValue(row.rssi)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {formatBytes(row.rx_bytes)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {formatBytes(row.tx_bytes)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {row.tx_failed != null && Number.isFinite(Number(row.tx_failed)) ? String(row.tx_failed) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {formatWifiRateDisplay(row.rx_rate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {formatWifiRateDisplay(row.tx_rate)}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => goToDevice(row.baseSerial)}
                        >
                          {row.baseName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                          {row.baseSerial}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </Paper>
      </>
      )}

      {overviewTab === 4 && (
        <FieldSurveyTab
          devices={devices}
          publicWifiMap={publicWifiMap}
          isLoaded={isLoaded}
          loadError={loadError}
          mapColorScheme={mapColorScheme}
          isActive={overviewTab === 4}
          canMutate={canMutate}
        />
      )}

      <PublicWifiMapSelectionDialog
        open={selectionDialogOpen}
        onClose={handleSelectionDialogClose}
        selectedDevices={polygonSelectedDevices}
      />

      <Dialog
        open={removeSsidDialogOpen}
        onClose={handleCloseRemoveSsidDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Public WiFi SSID</DialogTitle>
        <DialogContent>
          {removeSsidTarget?.device && removeSsidTarget?.action && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                Device: <strong>{removeSsidTarget.device.name || removeSsidTarget.device.serial_number}</strong>
              </Typography>
              <Typography variant="body2">
                SSID: <strong>{removeSsidTarget.action.ssidName}</strong>
              </Typography>
              {removeSsidTarget.action.type === 'direct' && (
                <Alert severity="info">
                  This SSID profile (<strong>{removeSsidTarget.action.profileName}</strong>) is assigned
                  {' '}<em>directly</em> to this device. Confirming will unassign the profile from the device
                  and disable Public WiFi on it.
                </Alert>
              )}
              {removeSsidTarget.action.type === 'group' && (
                <Alert severity="warning">
                  This SSID (<strong>{removeSsidTarget.action.profileName}</strong>) comes from group
                  {' '}<strong>{removeSsidTarget.action.groupName}</strong>. To remove it from this device,
                  the device will be removed from that group. Group SSID and radio profile settings will be
                  stripped from the device (unless it still receives them via another group or a direct assignment).
                </Alert>
              )}
              {removeSsidTarget.action.type === 'unknown' && (
                <Alert severity="warning">
                  The active SSID <strong>{removeSsidTarget.action.ssidName}</strong> could not be matched to a
                  known profile assignment (direct or via group). Use WiFi Network → Configuration to manage
                  SSID assignments manually.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseRemoveSsidDialog} disabled={removeSsidSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmRemoveSsid}
            disabled={!canMutate || removeSsidSaving || removeSsidTarget?.action?.type === 'unknown'}
            startIcon={removeSsidSaving ? <CircularProgress size={16} color="inherit" /> : <WifiOffIcon />}
          >
            {removeSsidSaving
              ? 'Removing…'
              : removeSsidTarget?.action?.type === 'group'
                ? 'Remove from Group'
                : 'Unassign SSID'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicWifiOverview;

